import * as React from "react";
import {cx} from 'emotion';

import "./table-view.scss";

import ReactTooltip         from 'react-tooltip';
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faExclamationTriangle,
    faMinus,
    faPlus,
    faTruckFast,
}                           from "@fortawesome/free-solid-svg-icons";

import {ApprovalRequest, ApprovalRequestSortMode}   from "../approval-requests";
import {
    ApprovalRequestAttribute,
    ApprovalRequestAttributesDict,
    ApprovalRequestAttributesService,
    ATTRIBUTE_KEY_LABELS,
}                                                   from "../approval-request-attributes";
import {PromoCodeAlert}                             from "../common/promo-code-alert";
import {RowExpansion}                               from "./row-expansion/row-expansion";

import {ComponentNext}  from "@Common/react/cst2";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {formatDate1}    from "@Common/ui-components/format-date";
import {DataTableD1}    from "@Common/ui-components/heavy/common/design2/data-table/data-table-d1";
import {formatMoney}    from "@Common/ui-components/language/format-money";
import {chain}          from "@Common/utils/fs";

import {IconTooltip}            from "@Xpresso/common/tool-tip";
import {formatServiceLevelName} from "@Xpresso/routes/customize/steps/checkout/shipping-section/common/service-level/format-service-level-name";

export const ApprovalRequestsTableView = ({
    approvalRequestEntries,
    attributeKeys,
    attributesService,
    expandedIds,
    onToggleAllExpanded : emitToggleAllExpanded,
    onToggleRowExpanded : emitToggleRowExpanded,
    sortModes,
    sortState           : {onChange: setActiveSort, value: activeSort},
}:{
    approvalRequestEntries  : Array<{approval: ApprovalRequest, attributes: ApprovalRequestAttributesDict}>,
    attributeKeys           : ApprovalRequestAttribute['key'][],
    attributesService       : ComponentNext<typeof ApprovalRequestAttributesService, "renderChildren">,
    expandedIds             : number[],
    onToggleAllExpanded     : () => void,
    onToggleRowExpanded     : (id:number) => void,
    sortModes               : ApprovalRequestSortMode[],
    sortState               : ComponentNext<typeof UseState<{ascending: boolean, name: string}>>,
}): JSX.Element => {
    const columnKeys = mapAttributeKeysAsColumnKeys(attributeKeys);
    return DataTableD1({
        className       : "table-view-sl33p",
        columns         : columnKeys.map(key => ({
            ...TABLE_COLUMN_TEMPLATES[key].makeHeader({
                expandedIds,
                sortModes,
                onToggleAllExpanded     : emitToggleAllExpanded,
            }),
            sortable    : chain(
                SORT_MODE_COLUMN_MATCHES.find(([, sortColumnKey]) => key === sortColumnKey) ?? [],
                ([sortName]) => sortName && getSortable({sortModes, sortName})
            )
        })),
        customSorting   : {
            apply                       : (rows) => rows,
            direction                   : activeSort.ascending ? "asc" : "desc",
            isSortingActiveForColumn    : ({columnIndex}) => (
                SORT_MODE_COLUMN_MATCHES.find(([sortKey]) => activeSort.name === sortKey)?.[1] === columnKeys[columnIndex]
            ),
            toggleColumn                : ({columnIndex}) => chain(
                columnKeys[columnIndex],
                (columnKey) => SORT_MODE_COLUMN_MATCHES.find(([, sortColumnKey]) => sortColumnKey === columnKey),
                ([sortName]) => sortModes.find(({name}) => name === sortName),
                (sortMode) => setActiveSort({
                    ascending   : activeSort.name === sortMode.name ? !activeSort.ascending : sortMode.defaultAscending,
                    name        : sortMode.name,
                })
            ),
            value                       : {
                columnIndex : columnKeys.indexOf(SORT_MODE_COLUMN_MATCHES.find(([sortName]) => sortName === activeSort.name)?.[1]),
                direction   : activeSort.ascending ? "asc" : "desc",
            },
        },
        rows                : approvalRequestEntries.map(({approval, attributes}) => {
            const expanded = expandedIds.includes(approval.id);
            return ({
                key         : approval.approvalGate + approval.id,
                className   : expanded && "expanded",
                columns     : columnKeys.map(key => TABLE_COLUMN_TEMPLATES[key].makeCell({
                    approval,
                    attributes,
                    expanded,
                    sortModes,
                    onToggleExpanded    : () => emitToggleRowExpanded(approval.id),
                })),
                domAttrs    : {
                    onClick: (e) => (
                        window.getSelection().isCollapsed
                        && e.target instanceof HTMLElement
                        && !["A", "BUTTON"].includes(e.target.tagName)
                        && emitToggleRowExpanded(approval.id)
                    )
                },
                expansion   : expanded ? {
                    render: ({colCount}) => (
                        <tr className="data-row-expansion"><td colSpan={colCount}>
                            {RowExpansion({
                                approval,
                                attributes,
                                ordersAttributes: (approval.orders as Array<Types.Utils.ArrayItem<typeof approval['orders']>>).map(order => attributesService.makeOrderAttributes(approval, order)),
                            })}
                        </td></tr>
                    ),
                } : null,
                xdQaName: approval.id
            });
        }),
    })
};

// Sort modes and columns may change in the future
const SORT_MODE_COLUMN_MATCHES:[sortName: string, columnName: string][] = [
    ["agent"        , "agent"],
    ["alerts"       , "alerts"],
    ["branch"       , "branch"],
    ["date"         , "date"],
    ["email"        , "email"],
    ["id"           , "id"],
    ["price"        , "price"],
];

const TABLE_COLUMN_TEMPLATES:{[columnName: string]: {
    makeHeader               : (props:{
        sortModes               : ApprovalRequestSortMode[],
        onToggleAllExpanded     : () => void,
        expandedIds             : number[],
    }) => Types.Utils.ArrayItem<Parameters<typeof DataTableD1>[0]['columns']>
    makeCell   : (props:{
        approval            : ApprovalRequest,
        attributes          : ApprovalRequestAttributesDict,
        expanded            : boolean,
        onToggleExpanded    : () => void,
        sortModes           : ApprovalRequestSortMode[],
    }) => Types.Utils.ArrayItem<Parameters<typeof DataTableD1>[0]['rows'][number]['columns']>,
}} = {
    actions : {
        makeHeader  : () => ({align: "center", label: "Action", domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "Action",
        }}),
        makeCell    : ({attributes: {approve, corrupted}}) => ({
            render: () => (
                approve
                    ? <div className="action-cell"><button onClick={approve.run} xd-qa-type="button" xd-qa-name={approve.label}>{approve.label}</button></div>
                    : corrupted
                        ? (
                            <>
                                <IconTooltip/>
                                <FontAwesomeIcon
                                    className={"alert-icon"}
                                    icon={faExclamationTriangle}
                                    data-tip={corrupted.message}
                                    onMouseOver={() => ReactTooltip.rebuild()}
                                />
                            </>
                        )
                        : (
                            <div className="action-cell" xd-qa-type="label" xd-qa-name="Order must be approved in your e-Procurement system.">
                                <div className="txtCenter">Order must be approved</div>
                                <div className="txtCenter">in your e-Procurement system.</div>
                            </div>
                        )
            )
        }),
    },
    agent: {
        makeHeader  : () => ({label: ATTRIBUTE_KEY_LABELS['agent'], domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "User",
        }}),
        makeCell    : ({attributes: {agent}}) => ({
            render      : () => agent && (
                agent.isBranchOrder
                    ? "Branch Order"
                    : <a onClick={agent.goToAssets} xd-qa-type="link" xd-qa-name="User Assets">{agent.value.name}</a>
            ),
        }),
    },
    alerts: {
        makeHeader  : () => ({label: "Alerts", domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "Alerts",
        }}),
        makeCell    : ({attributes: {hasFastShipping, hasPromoCode}}) => ({
            render  : () => (
                <div className="alerts-cell">
                    {hasPromoCode && <div><PromoCodeAlert code={hasPromoCode.code} expiry={hasPromoCode.expires}/></div>}
                    {hasFastShipping && <div><i className="alert-icon" aria-label="Expedited shipping" xd-qa-type="icon" xd-qa-name="Expedite Shipping"><FontAwesomeIcon icon={faTruckFast}/></i></div>}
                </div>
            ),
        }),
    },
    branch: {
        makeHeader  : () => ({label: ATTRIBUTE_KEY_LABELS['branch'], domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "Office",
        }}),
        makeCell    : ({attributes: {branch}}) => ({
            render      : () => <a onClick={branch?.goToAssets} xd-qa-type="link" xd-qa-name="Office Assets">{branch?.value.name ?? ""}</a>,
        }),
    },
    collapse: {
        makeHeader  : ({expandedIds, onToggleAllExpanded: emitToggleAllExpanded}) => ({
            align   : "center",
            render  : () => <FontAwesomeIcon className="icon" onClick={emitToggleAllExpanded} icon={expandedIds.length > 0 ? faMinus : faPlus} xd-qa-type="icon" xd-qa-name="Expand All"/>,
            domAttrs: {
                "xd-qa-type" : "label",
                "xd-qa-name" : "Collapse",
            },
        }),
        makeCell    : ({attributes: {corrupted}, expanded, onToggleExpanded: emitToggleExpanded}) => ({
            render  : () => !corrupted && <FontAwesomeIcon className={cx("icon", {expanded})} onClick={emitToggleExpanded} icon={faChevronDown} xd-qa-type="button" xd-qa-name="Expand"/>
        }),
    },
    date: {
        makeHeader  : () => ({label: ATTRIBUTE_KEY_LABELS['date'], domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "Date",
        }}),
        makeCell    : ({attributes: {date}}) => ({
            render      : () => <span xd-qa-type="text" xd-qa-name="Date">{date && formatDate1(date.value)}</span>,
        }),
    },
    delivery: {
        makeHeader  : () => ({label: "Delivery", domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "Delivery",
        }}),
        makeCell    : ({approval: {orders: [firstOrder]}, attributes: {delivery}}) => ({
            render  : () => <span xd-qa-type="text" xd-qa-name="Delivery Service">{delivery && firstOrder.deliveryService && (
                firstOrder.deliveryType === "shipping"
                    ? formatServiceLevelName(firstOrder.deliveryService)
                    : `${firstOrder.deliveryService} Class`
            )}</span>
        }),
    },
    email: {
        makeHeader  : () => ({label: "User Email", domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "User Email",
        }}),
        makeCell    : ({attributes}) => ({
            render      : () => attributes.email?.value ?? false,
        }),
    },
    id: {
        makeHeader  : () => ({label: ATTRIBUTE_KEY_LABELS['id'], domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "ID",
        }}),
        makeCell    : ({approval, attributes: {id}}) => ({
            render  : () => (
                <div className="id-cell">
                    <span hidden xd-qa-type="label" xd-qa-name="Row Type">{approval.approvalGate}</span>
                    <div className="type" xd-qa-type="text" xd-qa-name="Gate">
                        {id?.label ?? approval.approvalGate}
                    </div>
                    <div className="id" xd-qa-type="label" xd-qa-name="ID">
                        {id?.value ?? approval.id}
                    </div>
                </div>
            ),
        }),
    },
    price: {
        makeHeader  : () => ({align: "right", label: ATTRIBUTE_KEY_LABELS['price'], domAttrs: {
            "xd-qa-type" : "label",
            "xd-qa-name" : "Price",
        }}),
        makeCell    : ({attributes: {price}}) => ({
            render      : () => <span xd-qa-type="text" xd-qa-name="Price">{price && formatMoney(price.value)}</span>,
        }),
    },
};

const getSortable = ({sortModes, sortName}:{
    sortModes   : ApprovalRequestSortMode[],
    sortName    : string,
}) => {
    const targetSortMode = sortModes.find(sortMode => sortMode.name === sortName);
    return targetSortMode ? {defaultDirection: targetSortMode.defaultAscending ? "asc" as const : "desc" as const} : null;
};

const mapAttributeKeysAsColumnKeys = (attributeKeys: ApprovalRequestAttribute['key'][]): string[] => (
    [
        "collapse",
        "alerts",
        "id",
        "branch",
        "agent",
        "email",
        "delivery",
        "date",
        "price",
        "actions",
    ].filter(columnKey => (
        ["actions", "alerts", "collapse", "id"].includes(columnKey)
        || attributeKeys.includes(columnKey as ApprovalRequestAttribute['key'])
    ))
);

